import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {throttle} from 'lodash';
import {Button} from "@/components/UiElements/index";
import Spiral from "@/components/UiElements/Spiral";

interface PaginationProps {
    theme: (rowData: any) => React.ReactNode;
    total?: number;
    getPageData: (pageNumber: number) => any[] | Promise<any[]>;
    offset?: number;
    className?: string;
}

interface PaginationBarProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (pageNumber: number) => void;
    isPageEnabled?: (pageNumber: number) => boolean;
    label?: string;
    showNavigation?: boolean;
    hideWhenSingle?: boolean;
    className?: string;
}

export const PaginationBar = ({
    currentPage,
    totalPages,
    onPageChange,
    isPageEnabled = () => true,
    label = 'Page:',
    showNavigation = false,
    hideWhenSingle = true,
    className = '',
}: PaginationBarProps) => {
    if (totalPages < 1 || (hideWhenSingle && totalPages < 2)) return null;

    const canGoPrevious = currentPage > 1;
    const canGoNext = currentPage < totalPages;

    return (
        <div className={`transition-all w-full bg-white z-10 px-md text-text-md min-h-[45px] border-t border-t-gray-200 flex flex-row items-center gap-sm select-none text-gray-300 ${className}`}>
            {showNavigation && (
                <button
                    type="button"
                    aria-label="Previous preview"
                    disabled={!canGoPrevious}
                    onClick={() => canGoPrevious && onPageChange(currentPage - 1)}
                    className={`min-w-[88px] h-[32px] px-sm shrink-0 rounded-md flex items-center justify-center gap-xs text-xs transition-all ${canGoPrevious ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900' : 'text-gray-200 cursor-default'}`}
                >
                    <span className="font-icon-solid"></span>
                    <span>Previous</span>
                </button>
            )}

            <div className="flex flex-grow gap-xs items-center min-w-0">
                {label && <span className="pr-sm text-gray-400 transition-all whitespace-nowrap">{label}</span>}
                <span className="flex flex-grow gap-0 items-center overflow-hidden">
                    {Array.from({ length: totalPages }, (_, index) => {
                        const pageNumber = index + 1;
                        const enabled = isPageEnabled(pageNumber);

                        return (
                            <span
                                className={`w-[15px] h-[15px] shrink-0 flex justify-center items-center group rounded-full transition-all ${enabled ? 'cursor-pointer hover:bg-gray-900' : ''}`}
                                key={pageNumber}
                            >
                                <span
                                    role="button"
                                    aria-label={`Go to ${label ? label.toLowerCase().replace(':', '') : 'page'} ${pageNumber}`}
                                    className={`${currentPage === pageNumber ? 'text-primary bg-gray-500' : 'bg-gray-200'} ${enabled ? 'group-hover:bg-gray-950' : 'bg-transparent border border-gray-200'} transition-all rounded-full w-[10px] h-[10px]`}
                                    onClick={() => enabled && onPageChange(pageNumber)}
                                />
                            </span>
                        );
                    })}
                </span>
                <span className="whitespace-nowrap">({currentPage} / {totalPages})</span>
            </div>

            {showNavigation && (
                <button
                    type="button"
                    aria-label="Next preview"
                    disabled={!canGoNext}
                    onClick={() => canGoNext && onPageChange(currentPage + 1)}
                    className={`min-w-[68px] h-[32px] px-sm shrink-0 rounded-md flex items-center justify-center gap-xs text-xs transition-all ${canGoNext ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900' : 'text-gray-200 cursor-default'}`}
                >
                    <span>Next</span>
                    <span className="font-icon-solid"></span>
                </button>
            )}
        </div>
    );
};

interface PagerBarProps {
    currentPage: number;
    // Left undefined when the API cannot report a total (the orders list endpoint
    // returns page/size/contents only). The bar then pages forward off hasNext.
    totalPages?: number;
    hasNext: boolean;
    onPageChange: (pageNumber: number) => void;
    disabled?: boolean;
    className?: string;
}

/**
 * Server-side pager: one page of data is on screen at a time and every page
 * change is a fresh request. Unlike PaginationBar it does not assume the caller
 * already holds the whole data set, and it tolerates an unknown total.
 */
export const PagerBar = ({
    currentPage,
    totalPages,
    hasNext,
    onPageChange,
    disabled = false,
    className = '',
}: PagerBarProps) => {
    const hasKnownTotal = typeof totalPages === 'number' && totalPages > 0;
    // Nothing to page through: a known single page, or a first page with no more
    // results behind it.
    if (hasKnownTotal ? totalPages < 2 : (currentPage === 1 && !hasNext)) return null;

    const canGoPrevious = currentPage > 1 && !disabled;
    const canGoNext = (hasKnownTotal ? currentPage < totalPages : hasNext) && !disabled;
    const navButtonClassName = (enabled: boolean) => (
        `min-w-[88px] h-[32px] px-sm shrink-0 rounded-md flex items-center justify-center gap-xs text-xs transition-all ${
            enabled ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900' : 'text-gray-200 cursor-default'
        }`
    );

    return (
        <div className={`transition-all w-full bg-white z-10 px-md text-text-md min-h-[45px] border-t border-t-gray-200 flex flex-row items-center justify-end gap-sm select-none text-gray-300 ${className}`}>
            <button
                type="button"
                aria-label="Previous page"
                disabled={!canGoPrevious}
                onClick={() => canGoPrevious && onPageChange(currentPage - 1)}
                className={navButtonClassName(canGoPrevious)}
            >
                <span className="font-icon-solid"></span>
                <span>Previous</span>
            </button>

            <button
                type="button"
                aria-label="Next page"
                disabled={!canGoNext}
                onClick={() => canGoNext && onPageChange(currentPage + 1)}
                className={navButtonClassName(canGoNext)}
            >
                <span>Next</span>
                <span className="font-icon-solid"></span>
            </button>
        </div>
    );
};

export interface PaginationRef {
    goToPage: (pageNumber: number) => Promise<boolean>;
    scrollToTop: () => void;
    reset: () => Promise<boolean>;
    scrollToId: (id: string) => Promise<boolean>;
}

const Pagination = forwardRef<PaginationRef, PaginationProps>(
    ({ theme, total, getPageData, offset = 0, className="" }, ref) => {

    const [currentPage, setCurrentPage] = useState(1);
    const [pagesData, setPagesData] = useState<{ [key: number]: any[] }>({});
    const pagesDataRef = useRef<{ [key: number]: any[] }>({});
    const [loading, setLoading] = useState(false);
    const [nextPageButton, setNextPageButton] = useState(false);
    const [lastPage, setLastPage] = useState(false);
    const [visibilityMap, setVisibilityMap] = useState<Record<number, number>>({});


    const containerRef = useRef<HTMLDivElement>(null);

    const pageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    const totalPage = total || Object.entries(pagesData).length;




    // Function to load and store data for a specific page
    const loadPageData = useCallback(
        throttle(async (pageNumber: number) => {

            if (!pagesDataRef.current[pageNumber]) {
                setLoading(true);

                const pageData = getPageData(pageNumber);
                const resolvedData = pageData instanceof Promise ? await pageData : pageData;

                if (resolvedData.length > 0) {
                    pagesDataRef.current = { ...pagesDataRef.current, [pageNumber]: resolvedData };
                    setPagesData((prev) => ({
                        ...prev,
                        [pageNumber]: resolvedData,
                    }));
                    setCurrentPage(pageNumber);
                    scrollToPage(pageNumber);
                } else {
                    setLastPage(true);
                }
                setLoading(false);
            }

        }, 500),
        [getPageData]
    );





    // Handle infinite scrolling
    const handleScroll = useCallback(
        throttle(() => {
            if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 100) {
                nextPage();
            }
        }, 200),
        [currentPage, totalPage]
    );

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const nextPage = async () => {
        if(currentPage < 5 && (!total || total >= currentPage+1)) {
            const nextPage = currentPage + 1;
            await loadPageData(nextPage);
        } else {
            setNextPageButton(true);
        }
    };




    // Scroll to specific page
    const scrollToPage = (pageNumber: number) => {
        if (pageRefs.current[pageNumber]) {
            let top = pageRefs.current[pageNumber]!.offsetTop - offset;
            if (pageNumber === 1) {
                top = 0
            }
            window.scrollTo({
                top: top,
                behavior: 'smooth',
            });
        }
    };


    useEffect(() => {
        loadPageData(1); // Initial data load
        return () => {
            loadPageData.cancel?.();
        };
    }, [loadPageData]);




    // Observer to update currentPage as the user scrolls
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const newVisibilityMap: Record<number, number> = visibilityMap;

                entries.forEach((entry) => {
                    const pageIndex = Number(entry.target.getAttribute('data-page-index'));
                    newVisibilityMap[pageIndex] = entry.intersectionRatio;
                });

                // Update the state with the new visibility map
                setVisibilityMap((prev) => ({
                    ...prev,
                    ...newVisibilityMap,
                }));

                // Find the page with the highest visibility
                let maxVisiblePage = -1;
                let maxVisibility = 0;

                Object.keys(newVisibilityMap).forEach((key) => {
                    const pageIndex = parseInt(key, 10);
                    if (newVisibilityMap[pageIndex] > maxVisibility ) {
                        maxVisibility = newVisibilityMap[pageIndex];
                        maxVisiblePage = pageIndex;
                    }
                });

                // Update the current page to the page with the highest visibility
                if (maxVisiblePage !== -1) {
                    setCurrentPage(maxVisiblePage);
                }
            },
            {
                //rootMargin: `-${offset}px 0px -40px 0px`, // Adding offset for visibility detection
                rootMargin: `0px 0px 0px 0px`, // Adding offset for visibility detection
                threshold: Array.from({ length: 1000 }, (_, i) => i / 1000),
            }
        );

        // Observe each page element
        Object.values(pageRefs.current).forEach((pageRef) => {
            if (pageRef) observer.observe(pageRef);
        });

        return () => {
            // Unobserve each page element on cleanup
            Object.values(pageRefs.current).forEach((pageRef) => {
                if (pageRef) observer.unobserve(pageRef);
            });
        };
    }, [pagesData, offset]);




    const goToPageHandler = useCallback((pageNumber: number) => {

        if (pageNumber > 0 ) {
            loadPageData(pageNumber).then(() => {
                setCurrentPage(pageNumber);
                scrollToPage(pageNumber);
            });
        }
    },[])



    // Expose functions via ref
    useImperativeHandle(ref, () => ({
        goToPage: async (pageNumber: number) => {
            if (pageNumber > 0 && pageNumber <= totalPage) {
                await loadPageData(pageNumber);
                setCurrentPage(pageNumber);
                scrollToPage(pageNumber);
            }
            return true;
        },
        scrollToTop: () => {
            if (containerRef.current) {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            }
        },
        reset: async() => {
            loadPageData.cancel?.();
            pagesDataRef.current = {};
            setPagesData({});
            setCurrentPage(1);
            setLastPage(false);
            setLoading(false);
            setNextPageButton(false);
            // Load page 1 directly, bypassing throttle
            const pageData = getPageData(1);
            const resolvedData = pageData instanceof Promise ? await pageData : pageData;
            if (resolvedData && resolvedData.length > 0) {
                pagesDataRef.current = { 1: resolvedData };
                setPagesData({ 1: resolvedData });
            } else {
                setLastPage(true);
            }
            return true;
        },
        scrollToId: async (id: string): Promise<boolean> => {
            console.log('scrollToId', id);
            return new Promise((resolve) => {
                const checkIfGenerated = () => {
                    const element = document.getElementById(id);
                    console.log('element', element);
                    if (element) {
                        window.scrollTo({
                            top: element.offsetTop + offset,
                            behavior: 'smooth',
                        });
                        const checkIfScrollFinished = () => {
                            const isScrolledToTarget =
                                Math.abs(window.scrollY - (element.offsetTop + offset)) < 5;

                            if (isScrolledToTarget) {
                                window.removeEventListener('scroll', checkIfScrollFinished);
                                resolve(true);
                            }
                        };
                        window.addEventListener('scroll', checkIfScrollFinished);
                    } else if (!Object.keys(pagesData).length) {
                        setTimeout(checkIfGenerated, 50); // Retry after a short delay
                    } else {
                        resolve(false);
                    }
                };
                checkIfGenerated();
            });
        }


    }));

    return (
        <div className={`pagination-container flex flex-col pb-xxxl min-h-full ${className}`} ref={containerRef}>
            {/* Render all loaded pages */}
            <div className={"min-h-full flex-grow flex flex-col"}>
                {Object.entries(pagesData)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([pageIndex, pageData]) => (
                    <div
                        key={pageIndex}
                        id={`page-${pageIndex}`}
                        data-page-index={pageIndex}
                        ref={(el) => pageRefs.current[Number(pageIndex)] = el}
                    >
                        {pageData?.map((rowData, idx) => (
                            <React.Fragment key={idx}>{theme(rowData)}</React.Fragment>
                        ))}
                    </div>
                ))
            }
            </div>
            {/* Pagination controls */}




            {nextPageButton && !loading && (!total || total >= currentPage+1) && (
                <div className={"px-md pb-xxl pt-lg border-t border-t-gray-200 mt-xxl flex justify-start select-none"}>
                    <Button onClick={()=>loadPageData(currentPage+1)} type={"alt"}>
                        <span className={"flex flex-grow gap-md"}>
                            Load Page {String(currentPage+1)}
                            <span className={"font-icon-solid"}></span>
                        </span>
                    </Button>
                </div>
            )}




            {loading && (
                <div className={"px-md pb-xxl pt-lg border-t border-t-gray-200 mt-xxl flex justify-start select-none"}>
                    <span className={"flex flex-grow gap-md h-[40px] items-center text-text-md text-gray-400"}>
                        <Spiral/>
                        Loading next page...
                    </span>
                </div>
            )}


            {(lastPage || (total && currentPage >= total)) && (
                <div className={"px-md  mt-xxl  flex justify-start select-none"}>
                    <span className={"flex flex-grow gap-md h-[40px] items-center text-text-md text-gray-300"}>
                        You have reached the last page. No more data to load.
                    </span>
                </div>
            )}




            <PaginationBar
                currentPage={currentPage}
                totalPages={totalPage}
                onPageChange={goToPageHandler}
                isPageEnabled={(pageNumber) => Boolean(pagesData[pageNumber])}
                className="sticky bottom-0"
            />




        </div>
    );

});

Pagination.displayName = 'Pagination';
export default Pagination;
